<template>
  <div class="services-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="section-container">
        <h1 class="page-header__title">{{ t.title }}</h1>
        <div class="page-header__breadcrumb">
          <router-link to="/mira-solutions">Mira Solutions</router-link>
          <span>/</span>
          <span>{{ t.crumb }}</span>
        </div>
      </div>
    </div>

    <!-- Services List -->
    <section class="section">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            {{ t.intro.badge }}
          </span>
          <h2>{{ t.intro.title.pre }}<span class="text-gradient">{{ t.intro.title.hl }}</span>{{ t.intro.title.post }}</h2>
          <p class="section-subtitle">{{ t.intro.sub }}</p>
        </div>

        <div class="services-detail-grid">
          <div v-for="(service, index) in services" :key="service.key" class="service-detail-card animate-on-scroll"
            :class="'delay-' + ((index % 3) + 1)">
            <div class="service-detail-card__icon">
              <i :class="service.icon"></i>
            </div>
            <div class="service-detail-card__content">
              <h3>{{ service.title }}</h3>
              <p class="service-detail-card__tagline">{{ service.tagline }}</p>
              <p class="service-detail-card__desc">{{ service.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Process Section -->
    <section class="section section--alt">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            {{ t.process.badge }}
          </span>
          <h2>{{ t.process.title.pre }}<span class="text-gradient">{{ t.process.title.hl }}</span>{{ t.process.title.post }}</h2>
        </div>
        <div class="process-grid animate-on-scroll">
          <div class="process-step" v-for="(step, index) in t.process.steps" :key="index">
            <div class="process-step__number">{{ String(index + 1).padStart(2, '0') }}</div>
            <h4>{{ step.title }}</h4>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section section--gradient">
      <div class="section-container">
        <div class="services-cta animate-on-scroll">
          <h2>{{ t.cta.title }}</h2>
          <p>{{ t.cta.text }}</p>
          <router-link to="/contact" class="btn-primary-custom" style="background: #fff; color: #6C2973;">{{ t.cta.button }}</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { localize } from '@/i18n'
import copy from '@/locales/services'

const SERVICES = [
  { key: 'website', icon: 'fa fa-globe' },
  { key: 'apps', icon: 'fa fa-mobile' },
  { key: 'erp', icon: 'fa fa-cogs' },
  { key: 'crm', icon: 'fa fa-handshake-o' },
  { key: 'whatsapp', icon: 'fa fa-whatsapp' },
  { key: 'cloud', icon: 'fa fa-cloud' }
]

export default {
  name: 'ServicesPage',
  computed: {
    t() {
      return copy[this.$lang]
    },
    services() {
      return localize(SERVICES, this.t.items)
    }
  },
  mounted() {
    this.initScrollAnimations()
  },
  methods: {
    initScrollAnimations() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) { entry.target.classList.add('animated') }
          })
        },
        { threshold: 0.1 }
      )
      this.$nextTick(() => {
        document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el))
      })
    }
  }
}
</script>

<style scoped lang="scss">
.text-gradient {
  background: linear-gradient(135deg, #6C2973, #C73468);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.services-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.service-detail-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 28px;
  background: #fff;
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 4px 20px rgba(108, 41, 115, 0.06);
  border: 1px solid rgba(108, 41, 115, 0.06);
  transition: all 0.35s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 30px rgba(108, 41, 115, 0.12);
    border-color: rgba(199, 52, 104, 0.15);
  }
}

.service-detail-card__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(108, 41, 115, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #6C2973;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.service-detail-card:hover .service-detail-card__icon {
  background: linear-gradient(135deg, #6C2973, #C73468);
  color: #fff;
}

.service-detail-card__content {
  h3 {
    font-size: 1.25rem;
    margin-bottom: 6px;
  }
}

.service-detail-card__tagline {
  color: #C73468;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.service-detail-card__desc {
  color: $text-body;
  line-height: 1.8;
  font-size: 0.95rem;
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.process-step {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(108, 41, 115, 0.06);
  transition: all 0.3s ease;
  border: 1px solid rgba(108, 41, 115, 0.06);
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(108, 41, 115, 0.12);
  }

  h4 {
    margin: 16px 0 10px;
  }

  p {
    font-size: 0.88rem;
    color: $text-body;
    line-height: 1.7;
  }
}

.process-step__number {
  font-size: 2.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #6C2973, #C73468);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.services-cta {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  h2 {
    color: #fff;
    margin-bottom: 16px;
  }

  p {
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 28px;
  }
}

@media (max-width: 991px) {
  .process-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .service-detail-card {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 24px;
  }

  .process-grid {
    grid-template-columns: 1fr;
  }
}
</style>